import "@xyflow/react/dist/style.css";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import type { BooMapLink, ContainerRow } from "@/lib/api";
import { type LinkDestination, linkDestination } from "@/lib/boo-link-destination";
import { booConnections, booDepths, orderBoos } from "@/lib/boo-order";
import { cn } from "@/lib/utils";
import {
  Background,
  BackgroundVariant,
  BaseEdge,
  Controls,
  type Edge,
  EdgeLabelRenderer,
  type EdgeProps,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
} from "@xyflow/react";
import type { ElkNode } from "elkjs/lib/elk-api";
import { useNavigate, useParams } from "@tanstack/react-router";
import ELK from "elkjs/lib/elk.bundled.js";
import { ArrowRight, BookOpen, Clock, Wrench } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BooAccessLine } from "./BooAccessLine";
import { BooMark } from "./BooMark";
import { BooMenu } from "./BooMenu";
import { booStatus } from "./boo-status";

// The org chart of Boos. Each Boo sits above the Boos it uses skills and
// memories from, the way a team lead sits above the people they draw on. A
// Boo used by several Boos appears once, under each of them. Boos with no
// links sit beside the chart.

const elk = new ELK();
const BOO_W = 216;
const BOO_H = 76;
// Connectors into a Boo used by several Boos land this far apart, so each
// label sits on its own line.
const IN_PORT_GAP = 104;
const CHIP_STAGGER = 24;

type Point = { x: number; y: number };

type BooNodeData = { boo: ContainerRow };

// The Boos around the hovered one. Read by the cards through context so a
// hover never rebuilds the node list (React Flow re-measures rebuilt nodes).
const HoverContext = createContext<Set<string> | null>(null);
const useDimmed = (id: string) => {
  const related = useContext(HoverContext);
  return !!related && !related.has(id);
};
type BooNode = Node<BooNodeData, "boo">;

// The card menu's actions, through context for the same reason.
type ChartActions = {
  onLink?: (boo: ContainerRow) => void;
  onOpenSharing?: (boo: ContainerRow) => void;
};
const ActionsContext = createContext<ChartActions>({});

// Where each link's chip leads (null: neither Boo's Sharing page is open to
// the viewer, so the chip is just a label), and the ask before going there.
type LinkActions = {
  destination: (link: BooMapLink) => LinkDestination | null;
  // `from` is the chip, which gets focus back if the dialog is dismissed.
  open: (to: LinkDestination, from: HTMLElement) => void;
};
const LinkContext = createContext<LinkActions | null>(null);

type ChartEdgeData = {
  points: Point[];
  link: BooMapLink;
  // Position among the connectors entering the same Boo; labels stagger by it.
  chipIndex: number;
  highlighted: boolean;
  dimmed: boolean;
};
type ChartEdge = Edge<ChartEdgeData, "org">;

// ── Nodes ────────────────────────────────────────────────────────────────────

const hiddenHandle = "!size-1 !min-h-0 !min-w-0 !border-0 !bg-transparent";

function BooNodeCard({ id, data }: NodeProps<BooNode>) {
  const { boo } = data;
  const dimmed = useDimmed(id);
  const running = boo.deploymentStatus === "running";
  const status = booStatus(boo.deploymentStatus);
  const { onLink, onOpenSharing } = useContext(ActionsContext);
  return (
    <div
      className={cn(
        "group flex h-[76px] w-[216px] cursor-pointer flex-col justify-center gap-1.5 rounded-xl bg-card px-3.5 text-left",
        "shadow-[0_0_0_1px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05),0_6px_16px_-8px_rgba(0,0,0,0.12)]",
        "dark:shadow-[0_0_0_1px_rgba(255,255,255,0.09),0_1px_2px_rgba(0,0,0,0.4)]",
        "transition-[translate,box-shadow,opacity] duration-150 ease-out hover:-translate-y-px",
        "hover:shadow-[0_0_0_1px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06),0_12px_24px_-10px_rgba(0,0,0,0.18)]",
        boo.myRole && "ring-1 ring-foreground/15",
        !running && "bg-muted/40",
        dimmed && "opacity-40",
      )}
      title={boo.errorMessage ?? undefined}
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className={hiddenHandle}
      />
      <div className="flex min-w-0 items-center gap-2">
        <BooMark id={boo.id} muted={!running} className="size-6" />
        <span
          className="min-w-0 flex-1 truncate text-sm font-semibold leading-tight"
          title={boo.name}
        >
          {boo.name}
        </span>
        <span
          className={cn("size-2 shrink-0 rounded-full", status.dot)}
          aria-label={status.label ?? "Active"}
        />
        <BooMenu
          boo={boo}
          onLink={onLink}
          onOpenSharing={onOpenSharing}
          className="nodrag nopan size-6 after:absolute after:-inset-2"
        />
      </div>
      {/* The status word sits on the second row so the name keeps the first. */}
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1">
          <BooAccessLine container={boo} />
        </div>
        {status.label && (
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {status.label}
          </span>
        )}
        {/* A click anywhere on the card opens the Boo; this says so. */}
        <span className="flex shrink-0 items-center gap-0.5 text-[11px] font-medium text-muted-foreground transition-colors group-hover:text-foreground">
          Open
          <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
        </span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className={hiddenHandle}
      />
    </div>
  );
}

const nodeTypes = { boo: BooNodeCard };

// ── Edges ────────────────────────────────────────────────────────────────────

// Orthogonal route with rounded corners, like an org chart's connectors.
function roundedPath(points: Point[], radius = 10): string {
  if (points.length < 2) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    const next = points[i + 1];
    const inLen = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const outLen = Math.hypot(next.x - cur.x, next.y - cur.y);
    const r = Math.min(radius, inLen / 2, outLen / 2);
    const a = {
      x: cur.x - ((cur.x - prev.x) / (inLen || 1)) * r,
      y: cur.y - ((cur.y - prev.y) / (inLen || 1)) * r,
    };
    const b = {
      x: cur.x + ((next.x - cur.x) / (outLen || 1)) * r,
      y: cur.y + ((next.y - cur.y) / (outLen || 1)) * r,
    };
    d += ` L ${a.x} ${a.y} Q ${cur.x} ${cur.y} ${b.x} ${b.y}`;
  }
  const last = points[points.length - 1];
  return `${d} L ${last.x} ${last.y}`;
}

// The chip on a link: a button to the Sharing page that manages it, when the
// viewer can open one. `nodrag nopan` and the stopped events keep the canvas
// from panning or opening a Boo under it.
function LinkChip({ link }: { link: BooMapLink }) {
  const actions = useContext(LinkContext);
  const to = actions?.destination(link) ?? null;
  const requested = link.status === "requested";
  // Approval sets the link up on its own; this shows until it has.
  const parts = requested
    ? [{ icon: Clock, text: "Waiting for approval" }]
    : !link.setUp
      ? [{ icon: null, text: "Connecting" }]
      : [
          ...(link.skills ? [{ icon: Wrench, text: "Skills" }] : []),
          ...(link.memories ? [{ icon: BookOpen, text: "Memories" }] : []),
        ];
  const className = cn(
    "flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
    requested
      ? "gap-1 bg-amber-50 text-amber-800 shadow-[0_0_0_1px_rgba(217,119,6,0.25)] dark:bg-amber-950 dark:text-amber-200"
      : "gap-2 bg-background text-muted-foreground shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.05)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.12)]",
  );
  const content = parts.map(({ icon: Icon, text }) => (
    <span key={text} className="flex items-center gap-1">
      {Icon && <Icon className="size-3" aria-hidden />}
      {text}
    </span>
  ));
  if (!to || !actions) return <span className={className}>{content}</span>;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <button
      type="button"
      aria-label={`${parts.map((p) => p.text).join(", ")}. Open sharing for ${to.boo.name}`}
      title={`Open sharing for ${to.boo.name}`}
      onPointerDown={stop}
      onMouseDown={stop}
      onDoubleClick={stop}
      onClick={(e) => {
        e.stopPropagation();
        actions.open(to, e.currentTarget);
      }}
      className={cn(
        className,
        "nodrag nopan pointer-events-auto relative cursor-pointer outline-none after:absolute after:-inset-1.5",
        "transition-[translate,box-shadow,background-color,color] duration-150 ease-out hover:-translate-y-px active:translate-y-0",
        "focus-visible:ring-3 focus-visible:ring-ring/50",
        requested
          ? "hover:bg-amber-100 hover:shadow-[0_0_0_1px_rgba(217,119,6,0.4),0_2px_6px_-2px_rgba(217,119,6,0.3)] dark:hover:bg-amber-900"
          : "hover:text-foreground hover:shadow-[0_0_0_1px_rgba(0,0,0,0.14),0_2px_6px_-2px_rgba(0,0,0,0.15)] dark:hover:shadow-[0_0_0_1px_rgba(255,255,255,0.24)]",
      )}
    >
      {content}
    </button>
  );
}

function OrgChartEdge({ data, style }: EdgeProps<ChartEdge>) {
  if (!data || data.points.length < 2) return null;
  const end = data.points[data.points.length - 1];
  return (
    <>
      <BaseEdge
        path={roundedPath(data.points)}
        style={{
          ...style,
          stroke: "var(--muted-foreground)",
          strokeOpacity: data.highlighted ? 0.9 : data.dimmed ? 0.12 : 0.4,
          strokeWidth: data.highlighted ? 2 : 1.5,
          strokeDasharray:
            data.link.status === "requested" ? "5 4" : undefined,
          transition:
            "stroke-opacity 150ms ease-out, stroke-width 150ms ease-out",
        }}
      />
      <EdgeLabelRenderer>
        {/* What the Boo below gives the Boo above, on its own connector. */}
        <div
          className={cn(
            "pointer-events-none absolute transition-opacity duration-150",
            data.dimmed && "opacity-30",
          )}
          style={{
            transform: `translate(-50%, -50%) translate(${end.x}px, ${end.y - 20 - data.chipIndex * CHIP_STAGGER}px)`,
          }}
        >
          <LinkChip link={data.link} />
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const edgeTypes = { org: OrgChartEdge };

// ── Hierarchy ────────────────────────────────────────────────────────────────

type TreeEdge = {
  id: string;
  parent: string;
  child: string;
  link: BooMapLink;
};

/** Parent = the Boo that uses; child = the Boo it uses. */
function hierarchy(boos: ContainerRow[], links: BooMapLink[]): TreeEdge[] {
  const ids = new Set(boos.map((b) => b.id));
  return links
    .filter(
      (l) =>
        ids.has(l.sourceId) &&
        ids.has(l.consumerId) &&
        l.sourceId !== l.consumerId,
    )
    .map((l) => ({
      id: l.id,
      parent: l.consumerId,
      child: l.sourceId,
      link: l,
    }));
}

function useDarkMode(): boolean {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains("dark")),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

type Layout = {
  key: string;
  positions: Map<string, Point>;
  routes: Map<string, Point[]>;
  width: number;
  height: number;
};

const CHART_PAD = 48;

// Boos with no links sit in a grid: in columns beside the chart, or in rows
// of up to ROW_MAX when nothing is linked.
const STACK_PER_COLUMN = 6;
const ROW_MAX = 4;
const GRID_GAP = 24;
const BESIDE_GAP = 56;
const LAYER_GAP = 76;
// Below this the cards stop being readable; wider charts pan instead.
const MIN_FIT_ZOOM = 0.75;

export function BooOrgChart({
  boos,
  links,
  onOpen,
  onLink,
  onOpenSharing,
}: {
  boos: ContainerRow[];
  links: BooMapLink[];
  onOpen: (boo: ContainerRow) => void;
  // Card menu actions; the menu is hidden when neither is given.
  onLink?: (boo: ContainerRow) => void;
  onOpenSharing?: (boo: ContainerRow) => void;
}) {
  const dark = useDarkMode();
  const actions = useMemo(
    () => ({ onLink, onOpenSharing }),
    [onLink, onOpenSharing],
  );
  const tree = useMemo(() => hierarchy(boos, links), [boos, links]);
  // A link's chip opens the Sharing page that manages it, after asking.
  const navigate = useNavigate();
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  // The last chip picked stays put while the dialog fades out.
  const [leaving, setLeaving] = useState<LinkDestination | null>(null);
  const [confirming, setConfirming] = useState(false);
  const chipRef = useRef<HTMLElement | null>(null);
  const destination = useCallback(
    (link: BooMapLink) => (orgId ? linkDestination(link, boos) : null),
    [orgId, boos],
  );
  const linkActions = useMemo(
    () => ({
      destination,
      open: (to: LinkDestination, from: HTMLElement) => {
        chipRef.current = from;
        setLeaving(to);
        setConfirming(true);
      },
    }),
    [destination],
  );
  const goToSharing = () => {
    if (!leaving || !orgId) return;
    setConfirming(false);
    void navigate({
      to: "/orgs/$orgId/containers/$containerId/sharing",
      params: { orgId, containerId: leaving.boo.id },
      search: leaving.search,
    });
  };
  // Layout only reruns when the shape changes, so status polling doesn't
  // reshuffle the chart.
  const shapeKey = useMemo(
    () =>
      JSON.stringify([
        boos.map((b) => b.id),
        tree.map((e) => [e.id, e.parent, e.child]),
      ]),
    [boos, tree],
  );
  const [layout, setLayout] = useState<Layout | null>(null);
  const [failedShape, setFailedShape] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  // The panel's height follows the zoom the chart is drawn at, so a wide chart
  // doesn't sit small in a tall, mostly empty panel.
  const [panelWidth, setPanelWidth] = useState(0);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!panel) return;
    const observer = new ResizeObserver(([entry]) =>
      setPanelWidth(entry.contentRect.width),
    );
    observer.observe(panel);
    return () => observer.disconnect();
  }, [panel]);

  useEffect(() => {
    let cancelled = false;
    const [allIds, edges] = JSON.parse(shapeKey) as [
      string[],
      Array<[string, string, string]>,
    ];
    const linked = new Set(edges.flatMap(([, p, c]) => [p, c]));
    const ids = allIds.filter((id) => linked.has(id));
    const loose = allIds.filter((id) => !linked.has(id));

    const finish = (
      positions: Map<string, Point>,
      routes: Map<string, Point[]>,
      width: number,
      height: number,
    ) => {
      if (cancelled) return;
      if (loose.length) {
        const beside = ids.length > 0;
        const cols = beside
          ? Math.ceil(loose.length / STACK_PER_COLUMN)
          : Math.min(loose.length, ROW_MAX);
        const rows = Math.ceil(loose.length / cols);
        const x0 = beside ? width + BESIDE_GAP : 0;
        loose.forEach((id, i) => {
          const col = beside ? Math.floor(i / rows) : i % cols;
          const row = beside ? i % rows : Math.floor(i / cols);
          positions.set(id, {
            x: x0 + col * (BOO_W + GRID_GAP),
            y: row * (BOO_H + GRID_GAP),
          });
        });
        width = Math.max(width, x0 + cols * (BOO_W + GRID_GAP) - GRID_GAP);
        height = Math.max(height, rows * (BOO_H + GRID_GAP) - GRID_GAP);
      }
      setLayout({ key: shapeKey, positions, routes, width, height });
    };

    if (!ids.length) {
      finish(new Map(), new Map(), 0, 0);
      return () => {
        cancelled = true;
      };
    }

    // One top port per incoming connector so a Boo used by several Boos shows
    // each connector (and its label) separately.
    const incoming = new Map<string, string[]>();
    for (const [id, , child] of edges)
      incoming.set(child, [...(incoming.get(child) ?? []), id]);
    const node = (id: string) => {
      const ins = incoming.get(id) ?? [];
      return {
        id,
        width: BOO_W,
        height: BOO_H,
        layoutOptions: { "elk.portConstraints": "FIXED_POS" },
        ports: [
          {
            id: `${id}:out`,
            x: BOO_W / 2,
            y: BOO_H,
            width: 1,
            height: 1,
            layoutOptions: { "elk.port.side": "SOUTH" },
          },
          ...ins.map((edgeId, i) => ({
            id: `${id}:in:${edgeId}`,
            x: BOO_W / 2 + (i - (ins.length - 1) / 2) * IN_PORT_GAP,
            y: 0,
            width: 1,
            height: 1,
            layoutOptions: { "elk.port.side": "NORTH" },
          })),
        ],
      };
    };
    const graph: ElkNode = {
      id: "root",
      layoutOptions: {
        "elk.algorithm": "layered",
        "elk.direction": "DOWN",
        "elk.edgeRouting": "ORTHOGONAL",
        "elk.layered.mergeEdges": "true",
        "elk.layered.spacing.nodeNodeBetweenLayers": String(LAYER_GAP),
        "elk.layered.spacing.edgeNodeBetweenLayers": "22",
        "elk.spacing.nodeNode": "24",
        "elk.spacing.componentComponent": String(BESIDE_GAP),
        // Centres each Boo over the Boos it uses, like an org chart.
        "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
        "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
        // Org-chart depth: a Boo's level is its longest path from the top, so
        // Boos nobody uses stay on the first row even when they also use
        // something far below. Loops break from the top down.
        "elk.layered.layering.strategy": "LONGEST_PATH_SOURCE",
        "elk.layered.cycleBreaking.strategy": "DEPTH_FIRST",
      },
      children: ids.map(node),
      edges: edges.map(([id, parent, child]) => ({
        id,
        sources: [`${parent}:out`],
        targets: [`${child}:in:${id}`],
      })),
    };
    elk
      .layout(graph)
      .then((g) => {
        const children = g.children ?? [];
        finish(
          new Map(children.map((c) => [c.id, { x: c.x ?? 0, y: c.y ?? 0 }])),
          new Map(
            (g.edges ?? []).map((e) => {
              const sec = e.sections?.[0];
              return [
                e.id,
                sec
                  ? [sec.startPoint, ...(sec.bendPoints ?? []), sec.endPoint]
                  : [],
              ];
            }),
          ),
          Math.max(...children.map((c) => (c.x ?? 0) + BOO_W)),
          Math.max(...children.map((c) => (c.y ?? 0) + BOO_H)),
        );
      })
      .catch(() => {
        if (!cancelled) setFailedShape(shapeKey);
      });
    return () => {
      cancelled = true;
    };
  }, [shapeKey]);

  // Hovering a Boo lights up its connectors and the Boos at their other ends.
  const related = useMemo(() => {
    if (!hovered) return null;
    const set = new Set([hovered]);
    for (const e of tree)
      if (e.parent === hovered || e.child === hovered)
        set.add(e.parent).add(e.child);
    return set;
  }, [hovered, tree]);

  // Fixed width and height: React Flow treats a node without a measured size
  // as new and hides it until measured, so every status poll would flash the
  // chart. The cards have fixed sizes anyway.
  const nodes: BooNode[] = useMemo(() => {
    if (!layout) return [];
    return boos.map((boo) => ({
      id: boo.id,
      type: "boo" as const,
      position: layout.positions.get(boo.id) ?? { x: 0, y: 0 },
      width: BOO_W,
      height: BOO_H,
      data: { boo },
      draggable: false,
      selectable: false,
    }));
  }, [layout, boos]);

  const edges: ChartEdge[] = useMemo(() => {
    const seen = new Map<string, number>();
    return tree.map((e) => {
      const chipIndex = seen.get(e.child) ?? 0;
      seen.set(e.child, chipIndex + 1);
      const touches =
        !!hovered && (e.parent === hovered || e.child === hovered);
      return {
        id: e.id,
        type: "org",
        source: e.parent,
        target: e.child,
        data: {
          points: layout?.routes.get(e.id) ?? [],
          link: e.link,
          chipIndex,
          highlighted: touches,
          dimmed: !!hovered && !touches,
        },
      };
    });
  }, [tree, layout, hovered]);

  if (failedShape === shapeKey) return <BooOrgList boos={boos} links={links} onOpen={onOpen} />;
  if (!layout)
    return (
      <div
        className="h-72 animate-pulse rounded-xl bg-muted/40"
        aria-label="Drawing the chart"
      />
    );

  const zoom = panelWidth
    ? Math.min(
        1,
        Math.max(MIN_FIT_ZOOM, (panelWidth - CHART_PAD * 2) / layout.width),
      )
    : 1;
  // Grow with the chart so cards stay readable; the page scrolls, the chart doesn't.
  const height = Math.min(
    Math.max((layout.height + CHART_PAD * 2) * zoom, 240),
    1100,
  );
  return (
    <ActionsContext.Provider value={actions}>
      <LinkContext.Provider value={linkActions}>
        <HoverContext.Provider value={related}>
          <div
            ref={setPanel}
            className="overflow-hidden rounded-xl bg-muted/30 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
            style={{ height }}
          >
            <ReactFlow
              // fitView only runs on mount; remount when the panel settles to a new
              // height so the chart refits it.
              key={Math.round(height)}
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              colorMode={dark ? "dark" : "light"}
              fitView
              fitViewOptions={{ padding: 0.06, maxZoom: 1, minZoom: MIN_FIT_ZOOM }}
              minZoom={0.35}
              maxZoom={1.25}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              zoomOnScroll={false}
              panOnScroll={false}
              preventScrolling={false}
              proOptions={{ hideAttribution: true }}
              onNodeClick={(_e, node) => onOpen((node as BooNode).data.boo)}
              onNodeMouseEnter={(_e, node) => setHovered(node.id)}
              onNodeMouseLeave={() => setHovered(null)}
            >
              <Background
                variant={BackgroundVariant.Dots}
                gap={20}
                size={1}
                color="var(--border)"
              />
              <Controls
                showInteractive={false}
                position="bottom-right"
                className="!shadow-none"
              />
            </ReactFlow>
          </div>
        </HoverContext.Provider>
      </LinkContext.Provider>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Open sharing for ${leaving?.boo.name ?? "this Boo"}?`}
        description={leaving?.description ?? ""}
        confirmLabel="Open"
        variant="default"
        onConfirm={goToSharing}
        onCloseAutoFocus={(e) => {
          const chip = chipRef.current;
          if (!chip?.isConnected) return;
          e.preventDefault();
          chip.focus();
        }}
      />
    </ActionsContext.Provider>
  );
}

/** Phones: the same order as the chart, top to bottom, with who uses whom. */
export function BooOrgList({
  boos,
  links,
  onOpen,
}: {
  boos: ContainerRow[];
  links: BooMapLink[];
  onOpen: (boo: ContainerRow) => void;
}) {
  const name = new Map(boos.map((b) => [b.id, b.name]));
  const names = (ids: string[]) =>
    ids.map((id) => name.get(id) ?? "a Boo").join(", ");
  const depthOf = booDepths(links);
  const ordered = orderBoos(boos, links);
  return (
    <div className="flex flex-col gap-3">
      {ordered.map((boo) => {
        const { uses, usedBy, waiting } = booConnections(boo.id, links);
        return (
          <button
            key={boo.id}
            type="button"
            onClick={() => onOpen(boo)}
            style={{ marginLeft: Math.min(depthOf(boo.id), 3) * 16 }}
            className="flex flex-col gap-2 rounded-xl bg-card p-4 text-left shadow-[0_0_0_1px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-[scale] active:scale-[0.98] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.09)]"
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <BooMark
                id={boo.id}
                muted={boo.deploymentStatus !== "running"}
                className="size-6"
              />
              <span className="truncate font-semibold">{boo.name}</span>
            </span>
            <BooAccessLine container={boo} />
            {uses.length > 0 && (
              <span className="text-sm text-muted-foreground">
                Learns from {names(uses)}
              </span>
            )}
            {usedBy.length > 0 && (
              <span className="text-sm text-muted-foreground">
                Shares with {names(usedBy)}
              </span>
            )}
            {waiting > 0 && (
              <span className="text-sm text-muted-foreground">
                {waiting} {waiting === 1 ? "request" : "requests"} waiting for
                approval
              </span>
            )}
            <div>
              <StatusBadge
                status={boo.deploymentStatus}
                errorMessage={boo.errorMessage}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}
